import { useTraderTime } from '../contexts/TraderTimeContext';
import { formatCardNumber } from '../lib/utils';
import { bankDisplayName } from '../lib/kgsBanks';
import React, { useState, useEffect } from 'react';
import { ordersAPI, OrderResponse } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { Edit, Loader2, RefreshCw, X, TrendingUp, TrendingDown, List } from 'lucide-react';
import { formatAmount as formatAmountUtil } from '../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EditOrderDialog } from '../components/orders/EditOrderDialog';
import { OrderAutomation } from '../components/orders/OrderAutomation';
import { Switch } from '../components/ui/switch';
import { TRADER_REQUISITES_REFRESH_INTERVAL_MS } from '../lib/polling';

export const AllRequisitesPage: React.FC = () => {
    const { formatDate } = useTraderTime();
    const [orders, setOrders] = useState<OrderResponse[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);
    const [updatingPauseOrder, setUpdatingPauseOrder] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<OrderResponse | null>(null);
    const [activeTab, setActiveTab] = useState<'all' | 'deposit' | 'withdraw'>('all');

    const loadOrders = async (showLoading = true, page: number = currentPage) => {
        try {
            if (showLoading) {
                setIsLoading(true);
            }
            setError(null);
            const response = await ordersAPI.getMyOrders(page, pageSize);

            // Фильтруем только активные ордера
            const activeOrders = response.orders.filter(order => order.status === 'active');

            // Обновляем только если данные изменились
            setOrders(prevOrders => {
                const dataString = JSON.stringify(activeOrders);
                const prevString = JSON.stringify(prevOrders);

                if (dataString === prevString) {
                    return prevOrders;
                }

                return activeOrders;
            });

            setTotalPages(response.pages);
            setTotalItems(response.total);
            setCurrentPage(response.page);
        } catch (err: any) {
            setError('Не удалось загрузить реквизиты');
            console.error('Error loading orders:', err);
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadOrders(true, 1);
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        loadOrders(true, page);
    };

    // Автоматическое фоновое обновление накопленных сумм по реквизитам
    useEffect(() => {
        const interval = setInterval(() => {
            loadOrders(false); // Фоновое обновление без индикатора
        }, TRADER_REQUISITES_REFRESH_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    const handleCancelOrder = async (orderId: string) => {
        try {
            setCancellingOrder(orderId);
            await ordersAPI.cancelOrder(orderId);
            await loadOrders();
        } catch (err: any) {
            setError('Не удалось отменить ордер');
            console.error('Error cancelling order:', err);
        } finally {
            setCancellingOrder(null);
        }
    };

    const handleTogglePause = async (order: OrderResponse) => {
        try {
            setUpdatingPauseOrder(order.id);
            await ordersAPI.updateOrder(order.id, { is_paused: !order.is_paused });
            await loadOrders(false);
        } catch (err: any) {
            setError('Не удалось обновить паузу');
        } finally {
            setUpdatingPauseOrder(null);
        }
    };


    const formatAmount = (amount: number, currency?: string) => {
        return formatAmountUtil(amount, currency);
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            active: { label: 'Активный', variant: 'default' as const },
            completed: { label: 'Завершен', variant: 'secondary' as const },
            cancelled: { label: 'Отменен', variant: 'destructive' as const },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || {
            label: status,
            variant: 'secondary' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const renderOrderCard = (order: OrderResponse) => {
        const isDeposit = order.order_type === 'deposit';

        return (
            <div
                key={order.id}
                className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${order.is_paused ? 'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/40' : 'border-gray-200 dark:border-slate-700'}`}
            >
                <div className="flex flex-wrap items-start gap-4">
                    <div className="min-w-0 flex-[3_1_28rem] break-words">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            {isDeposit ? (
                                <>
                                    <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-300" />
                                    <span className="font-medium text-green-700 dark:text-green-300">📥 Пополнение</span>
                                </>
                            ) : (
                                <>
                                    <TrendingDown className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                                    <span className="font-medium text-blue-700 dark:text-blue-300">📤 Вывод</span>
                                </>
                            )}
                            {getStatusBadge(order.status)}
                            {order.is_paused && (
                                <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-200">
                                    На паузе
                                </Badge>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm [&>div]:min-w-0">
                            <div>
                                <p className="text-gray-600 dark:text-slate-300">Объём ордера:</p>
                                <p className="font-medium">
                                    {formatAmount(order.from_amount)} {order.from_currency}
                            </p>
                            <p className="text-gray-600 dark:text-slate-300">
                                Остаток: {formatAmount(order.remaining_fiat_amount ?? Math.max(0, order.from_amount - Math.max(order.reserved_amount || 0, order.accumulated_amount || 0)))} {order.from_currency}
                                </p>
                            </div>

                            {isDeposit && order.min_amount && order.max_amount && (
                                <div>
                                    <p className="text-gray-600 dark:text-slate-300">Диапазон платежей:</p>
                                    <p className="font-medium">
                                        {formatAmount(order.min_amount)} - {formatAmount(order.max_amount)} {order.from_currency}
                                    </p>
                                </div>
                            )}

                            <div>
                                <p className="text-gray-600 dark:text-slate-300">Создан:</p>
                                <p>{formatDate(order.created_at)}</p>
                            </div>

                            <div>
                                <p className="text-gray-600 dark:text-slate-300">ID:</p>
                                <p className="font-mono text-xs break-all">{order.id}</p>
                            </div>

                            {/* Реквизиты - только для пополнения */}
                            {isDeposit && (
                                <>
                                    {order.phone_number && (
                                        <div>
                                            <p className="text-gray-600 dark:text-slate-300">Телефон:</p>
                                            <p className="font-medium">{order.phone_number}</p>
                                        </div>
                                    )}

                                    {order.recipient_bank && (
                                        <div>
                                            <p className="text-gray-600 dark:text-slate-300">Банк:</p>
                                            <p className="font-medium">{bankDisplayName(order.recipient_bank)}</p>
                                        </div>
                                    )}

                                    {order.recipient_name && (
                                        <div>
                                            <p className="text-gray-600 dark:text-slate-300">Получатель:</p>
                                            <p className="font-medium">{order.recipient_name}</p>
                                        </div>
                                    )}

                                    {order.recipient_card && (
                                        <div className="sm:col-span-2">
                                            <p className="text-gray-600 dark:text-slate-300">Номер карты:</p>
                                            <p className="font-medium font-mono">{formatCardNumber(order.recipient_card)}</p>
                                        </div>
                                    )}

                                    {order.notes && (
                                        <div className="sm:col-span-2">
                                            <p className="text-gray-600 dark:text-slate-300">Примечание:</p>
                                            <p className="font-medium text-sm bg-gray-50 dark:bg-slate-900 p-2 rounded border-l-4 border-blue-500">
                                                {order.notes}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Прогресс заполнения */}
                            {order.accumulated_amount !== undefined && (
                                <div className="sm:col-span-2">
                                    <p className="text-gray-600 dark:text-slate-300 mb-2">Прогресс заполнения:</p>
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-sm">
                                            <span>
                                                {formatAmount(order.accumulated_amount)} / {formatAmount(order.from_amount)} {order.from_currency}
                                            </span>
                                            <span className="font-medium">
                                                {((order.accumulated_amount / order.from_amount) * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full transition-all"
                                                style={{
                                                    width: `${Math.min((order.accumulated_amount / order.from_amount) * 100, 100)}%`,
                                                }}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-slate-400">
                                            Свободно: {formatAmount(order.remaining_fiat_amount ?? Math.max(0, order.from_amount - Math.max(order.reserved_amount || 0, order.accumulated_amount || 0)))} {order.from_currency}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-[1_1_16rem] flex-col items-end gap-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                            <span>{order.is_paused ? 'На паузе' : 'Прием заявок'}</span>
                            <Switch
                                checked={!order.is_paused}
                                onCheckedChange={() => handleTogglePause(order)}
                                disabled={updatingPauseOrder === order.id}
                            />
                        </div>
                        {order.order_type === 'deposit' && (
                            <OrderAutomation order={order} onUpdated={() => loadOrders(false)} />
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingOrder(order)}
                        >
                            <Edit className="h-4 w-4" />
                            Редактировать
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelOrder(order.id)}
                            disabled={cancellingOrder === order.id}
                            className="text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                        >
                            {cancellingOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <X className="h-4 w-4" />
                            )}
                            Отменить
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // Фильтрация ордеров по типу
    const filteredOrders = orders.filter(order => {
        if (activeTab === 'all') return true;
        return order.order_type === activeTab;
    });

    const depositCount = orders.filter(o => o.order_type === 'deposit').length;
    const withdrawCount = orders.filter(o => o.order_type === 'withdraw').length;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                    Все рабочие реквизиты
                </h1>
                <p className="text-gray-600 dark:text-slate-300 mt-2">
                    Просмотр всех активных ордеров на пополнение и вывод
                </p>
            </div>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {error}
                </div>
            )}

            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>
                        Активные ордера ({filteredOrders.length})
                    </CardTitle>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadOrders(true)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                    </Button>
                </CardHeader>
                <CardContent>
                    <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                        <TabsList className="flex h-auto w-full flex-wrap mb-6 [&>button]:flex-1">
                            <TabsTrigger value="all">
                                Все ({orders.length})
                            </TabsTrigger>
                            <TabsTrigger value="deposit">
                                📥 Пополнение ({depositCount})
                            </TabsTrigger>
                            <TabsTrigger value="withdraw">
                                📤 Вывод ({withdrawCount})
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value={activeTab}>
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-300" />
                                    <span className="ml-2 text-gray-600 dark:text-slate-300">Загружаем реквизиты...</span>
                                </div>
                            ) : filteredOrders.length === 0 ? (
                                <div className="text-center py-12 text-gray-500 dark:text-slate-400">
                                    <List className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <p className="text-lg font-medium">Нет активных реквизитов</p>
                                    <p className="text-sm mt-2">
                                        {activeTab === 'all'
                                            ? 'Создайте ордер на пополнение или вывод'
                                            : activeTab === 'deposit'
                                                ? 'Создайте ордер на пополнение в разделе "Реквизиты для пополнения"'
                                                : 'Создайте ордер на вывод в разделе "Реквизиты для вывода"'
                                        }
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {filteredOrders.map(order => renderOrderCard(order))}

                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
            <EditOrderDialog
                order={editingOrder}
                open={Boolean(editingOrder)}
                onOpenChange={(open) => {
                    if (!open) setEditingOrder(null);
                }}
                onSaved={() => loadOrders(true)}
            />
        </div>
    );
};
